import { ProjectTemperatureData } from '../models/project-temperature-data';
import { DISABLED_THRESHOLD, isEnabled } from './curing-accumulators';

/**
 * Heat-input stages, resolved from whichever spelling the API actually sent.
 *
 * READ LONG, THEN SHORT. The same quantity has two names on the wire and the
 * short one is a decoy: `HMC: [{HT, HM}]` has been on the DTO for years and its
 * only populator builds an empty list, so every response to date carried `[]`.
 * The long `HeatInput: [{Temperature, Minutes}]` is the one DataServices fills
 * (c246b2c). Reading the short spelling alone is exactly why the curing chart
 * drew three legend entries where the desktop draws seven.
 *
 * This is the SAME trap the desktop archive has with CRB/CRE and LSS/LES, where
 * the short keys are null and the long ChannelRange / LinerStartSample carry the
 * geometry. Read long-then-short, never short alone.
 *
 * `[]` IS A REAL STATE, NOT A FAILURE. 59 of 80 desktop projects declare no
 * stages at all because none were configured for that cure. Callers render that
 * as "none configured"; nothing here throws or substitutes a default.
 */
export interface HeatInputStageResolved {
  /** Threshold in the CALLER'S display unit — the API converted it already. */
  temperature: number;
  /** Required dwell in minutes. 0 / absent means the stage declares no dwell. */
  minutes: number;
}

/** The chart draws at most four stages; the desktop's own series count. */
export const MAX_HEAT_INPUT_STAGES = 4;

/**
 * The stages in force for this project, ascending by temperature.
 *
 * Ascending is not cosmetic: a lower threshold is met for longer, so its mountain
 * is both taller and wider. Drawing ascending means each band is added before the
 * shorter ones that nest inside it, which is what lets all four be seen at once.
 */
export function heatInputStages(
  data: ProjectTemperatureData | null | undefined
): HeatInputStageResolved[] {
  if (!data) {
    return [];
  }

  // BOTH CASINGS, and this is not defensive padding — it is the shape actually
  // on the wire.
  //
  // DataServices serialises this response through
  // PerfHeaders.WriteJsonWithPerfAsync with
  // `PropertyNamingPolicy = JsonNamingPolicy.CamelCase`. An explicit
  // [JsonPropertyName] beats the policy, which is why `TRC`, `CT`, `HMC` and the
  // rest keep their short upper-case names — and why `HeatInput`, which carries
  // NO attribute, does not. It goes out as
  //
  //     "heatInput": [ { "temperature": 48.88889, "minutes": 300 }, ... ]
  //
  // Reading only the PascalCase spelling finds nothing and draws no bands: the
  // same silent-empty failure as HMC, one layer further out. Both are accepted
  // so that adding [JsonPropertyName("HeatInput")] on the server later is a
  // no-op here rather than a second outage.
  const bag = data as unknown as Record<string, unknown>;
  const rawLong = bag['HeatInput'] ?? bag['heatInput'];
  const long = Array.isArray(rawLong)
    ? (rawLong as Array<Record<string, unknown>>).map((h) => ({
        temperature: Number(h?.['Temperature'] ?? h?.['temperature']),
        minutes: Math.max(
          0,
          Math.trunc(Number(h?.['Minutes'] ?? h?.['minutes']) || 0)
        ),
      }))
    : [];

  // HMC carries an explicit [JsonPropertyName], so the policy does not touch it
  // and its members keep HT/HM. Lower-case is accepted anyway, for the same
  // reason as above and at the same cost.
  const rawShort = bag['HMC'] ?? bag['hmc'];
  const short = Array.isArray(rawShort)
    ? (rawShort as Array<Record<string, unknown>>).map((h) => ({
        temperature: Number(h?.['HT'] ?? h?.['ht']),
        minutes: Math.max(0, Math.trunc(Number(h?.['HM'] ?? h?.['hm']) || 0)),
      }))
    : [];

  const source: HeatInputStageResolved[] = long.length > 0 ? long : short;

  return source
    // A threshold at or above DISABLED_THRESHOLD is the existing "slot off"
    // convention, and a non-finite one is a malformed record. Neither is drawn.
    .filter((s: HeatInputStageResolved) => isEnabled(s.temperature))
    .sort((a: HeatInputStageResolved, b: HeatInputStageResolved) => a.temperature - b.temperature)
    .slice(0, MAX_HEAT_INPUT_STAGES);
}

/**
 * The four thresholds in the fixed X1..X4 slots CuringAccumulators expects,
 * padded with DISABLED_THRESHOLD so an absent slot folds nothing.
 */
export function heatInputThresholdSlots(
  stages: readonly HeatInputStageResolved[]
): [number, number, number, number] {
  const at = (i: number) =>
    stages.length > i ? stages[i]!.temperature : DISABLED_THRESHOLD;
  return [at(0), at(1), at(2), at(3)];
}

/**
 * Legend text for a heat-input stage: `Time over 48.9°C | 300min`.
 *
 * ONE DECIMAL, ALWAYS, for a stage. The real thresholds are 48.88889 / 54.44445 /
 * 60 / 65.55556 C and the desktop legend renders every one of them to 1dp —
 * including the whole one, as `60.0°C`. Dropping the decimal on 60 would make the
 * four entries disagree with each other about their own precision.
 *
 * THE DWELL SUFFIX IS CONDITIONAL. ` | {N}min` is appended only when a dwell
 * exists. A stage with no dwell must read `Time over 70.0°C`, never
 * `Time over 70.0°C | 0min` — "held for zero minutes" is a requirement nobody
 * set, rendered as though somebody had.
 */
export function heatInputSeriesName(
  temperature: number,
  minutes: number,
  unitSuffix: string
): string {
  const dwell = minutes > 0 ? ` | ${minutes}min` : '';
  return `Time over ${temperature.toFixed(1)}${unitSuffix}${dwell}`;
}

/**
 * Legend text for the CURING series: `Time over 70°C`.
 *
 * Deliberately NOT the 1dp format above. CuringTemperature is a whole number on
 * the DTO and the desktop legend prints it plain, so forcing `70.0°C` here would
 * invent a precision the value does not carry. The what-if axis badge is the
 * exception and does use 1dp — it tracks a dragged line, where the tenth is the
 * thing that changes.
 */
export function curingSeriesName(
  temperature: number,
  unitSuffix: string
): string {
  return `Time over ${formatPlain(temperature)}${unitSuffix}`;
}

/** Trailing-zero-free number: 70 -> "70", 70.5 -> "70.5". */
function formatPlain(value: number): string {
  if (!Number.isFinite(value)) {
    return '';
  }
  return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(1)));
}

/**
 * Does this set of stage temperatures look like it lost its fractional part in
 * transit? Reported, never corrected — the fix belongs on the server.
 *
 * WHY IT IS WORTH DETECTING AT ALL. The canonical stages are 48.88889 / 54.44445
 * / 60 / 65.55556 C, which are exactly 120 / 130 / 140 / 150 F. Truncated to int
 * they become 48 / 54 / 60 / 65 — visibly ~0.9 C low in metric, and in imperial
 * they land on 118.4 / 129.2 / 140 / 149 F, which still LOOKS like a tidy set. A
 * bug that is obvious in one unit and nearly invisible in the other is the kind
 * that ships.
 *
 * The test is deliberately narrow: every temperature an integer AND at least one
 * of them within half a degree of a canonical fractional threshold. A project
 * genuinely configured at whole degrees (70, 80) does not match.
 */
const CANONICAL_FRACTIONAL_C = [48.88889, 54.44445, 65.55556];

export function looksTruncated(
  stages: readonly HeatInputStageResolved[],
  isMetric: boolean
): boolean {
  if (!isMetric || stages.length === 0) {
    return false;
  }
  if (!stages.every((s) => Number.isInteger(s.temperature))) {
    return false;
  }
  return stages.some((s) =>
    CANONICAL_FRACTIONAL_C.some((c) => Math.abs(s.temperature - c) < 1)
  );
}
